import React from 'react';
import Chart from 'Shared/Chart/Chart';
import { usePullDatasourceTS2Query } from 'Services/api/reportsApi';
import { useGetMeteocodesByCodesStringsQuery } from 'Services/api/meteoCodesApi';
import ChartWrapper from 'Shared/Chart/ChartWrapper';
import {
  extractMeteocodesIds, getChartData, getChartScales, getQuery, getTimeUtcDataConverterByZone,
} from 'Features/StationsNew/Datasources/Datasource/Monitors/utils';
import { ChartType, ChartWidgetOptions } from 'Features/StationsNew/Datasources/Datasource/Monitors/types';

const msInHour = 1000 * 60 * 60;

type SimpleChartWidgetProps = {
  options: ChartWidgetOptions,
  title: string
  type?: ChartType
  zone: string
  pollingInterval: number
}

export function SimpleChartWidget({
  options,
  title,
  type = 'line',
  zone,
  pollingInterval,
}: SimpleChartWidgetProps) {
  const { datasetsInfo } = options;
  const meteocodesIds = React.useMemo(() => extractMeteocodesIds(datasetsInfo), [datasetsInfo]);
  const meteocodesQuery = useGetMeteocodesByCodesStringsQuery(meteocodesIds);
  const query = React.useMemo(() => getQuery(options), [options]);
  const result = usePullDatasourceTS2Query(query, {
    pollingInterval,
    skip: !datasetsInfo.length,
  });

  const meteocodes = React.useMemo(() => meteocodesQuery.data ?? [], [meteocodesQuery.data]);
  const chartsData = React.useMemo(() => result.data ?? [], [result.data]);

  const { firstYKey, chartjsScales }: any = React.useMemo(() => (
    getChartScales(datasetsInfo, meteocodes) || {}
  ), [datasetsInfo, meteocodes]);

  const convertData = React.useMemo(() => getTimeUtcDataConverterByZone(zone), [zone]);

  const chartData = React.useMemo(
    () => getChartData(datasetsInfo, chartsData, meteocodes, firstYKey, convertData),
    [chartsData, datasetsInfo, firstYKey, meteocodes, convertData],
  );

  const isLoading = meteocodesQuery.isLoading || result.isLoading;

  return (
    <ChartWrapper>
      {(width: number) => (
        <Chart
          chartTitle={title}
          chartOptions={{ width, height: 350, confines: [] }}
          datasets={chartData.datasets || []}
          scales={chartjsScales}
          loadOnClick={isLoading}
          loadOnClickOptions={{
            isLoading,
            onClick: null,
            isSuccess: false,
          }}
          minZoomRange={msInHour}
          type={type}
          timeFormatPostfix={`UTC${zone}`}
        />
      )}
    </ChartWrapper>
  );
}
